"use client";

import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ApiError, api, loadTokens } from "@/lib/api";

/**
 * 2026-04-30 — small bookmark toggle on a market card / detail row.
 * Hidden when signed-out.
 *
 * Hydration: a single shared cache loads /v1/me/bookmarks/ids once
 * per page-load, so N buttons rendered together cost one API
 * round-trip instead of N. A page that mounts buttons after the
 * first batch (filter change, infinite scroll) reuses the cached
 * promise. Cache resets on the granbay.balance.changed window event
 * (good-enough proxy for "user state changed" — also fired on
 * logout / login).
 *
 * Optimistic flip on click; rolls back on non-409 failure. 409 means
 * the server thinks we're already bookmarked — accept that.
 */

// Module-scope cache so all BookmarkToggleButton instances on a page
// share one fetch. Storing the promise (not the resolved set) means
// concurrent mounts dedupe to one in-flight request.
let bookmarkIdsCache: Promise<Set<string>> | null = null;

export function getBookmarkIds(): Promise<Set<string>> {
  if (!bookmarkIdsCache) {
    bookmarkIdsCache = api
      .listBookmarkIds()
      .then((res) => new Set(res.marketIds))
      .catch(() => new Set<string>());
  }
  return bookmarkIdsCache;
}

export function invalidateBookmarkIds() {
  bookmarkIdsCache = null;
}

export function BookmarkToggleButton({
  marketId,
  className,
}: {
  marketId: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const [authed, setAuthed] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAuthed(loadTokens() !== null);
  }, []);

  // Hydrate from the shared cache once we know we're authed.
  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    void getBookmarkIds().then((ids) => {
      if (!cancelled) setBookmarked(ids.has(marketId));
    });
    return () => {
      cancelled = true;
    };
  }, [authed, marketId]);

  // Invalidate on auth/balance changes so a logout/login swap doesn't
  // leak the previous user's bookmark set into the next session.
  useEffect(() => {
    const onChange = () => invalidateBookmarkIds();
    window.addEventListener("granbay.balance.changed", onChange);
    return () =>
      window.removeEventListener("granbay.balance.changed", onChange);
  }, []);

  if (!authed) return null;

  async function handleClick(e: React.MouseEvent) {
    // Sibling-of-Link layout means we don't have to fight
    // propagation, but the parent card may have a hover state
    // that bubbles. Defensive stop.
    e.preventDefault();
    e.stopPropagation();
    if (submitting) return;
    setSubmitting(true);
    const next = !bookmarked;
    setBookmarked(next); // optimistic
    try {
      if (next) await api.addBookmark(marketId);
      else await api.removeBookmark(marketId);
      // Keep the cache in sync so peer buttons reading the same set
      // see the change without a refetch.
      const ids = await getBookmarkIds();
      if (next) ids.add(marketId);
      else ids.delete(marketId);
    } catch (err) {
      // Roll back on failure. ApiError.status===409 means we tried
      // to add a duplicate — accept that as already-bookmarked.
      if (err instanceof ApiError && err.status === 409) {
        setBookmarked(true);
      } else {
        setBookmarked(!next);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={submitting}
      aria-label={
        bookmarked ? t("bookmarkToggle.remove") : t("bookmarkToggle.add")
      }
      aria-pressed={bookmarked}
      className={
        className ??
        // 44×44 on mobile (Apple HIG min tap target), 28×28 on
        // desktop where pointer precision is higher and the chrome
        // density matters more.
        "flex h-11 w-11 items-center justify-center rounded-md border transition-colors disabled:opacity-50 md:h-7 md:w-7"
      }
      style={{
        borderColor: bookmarked
          ? "var(--color-brand)"
          : "var(--color-border)",
        background: bookmarked
          ? "var(--color-brand-tint)"
          : "var(--color-surface)",
        color: bookmarked
          ? "var(--color-brand)"
          : "var(--color-text3)",
      }}
    >
      <Star
        size={14}
        strokeWidth={1.75}
        fill={bookmarked ? "currentColor" : "none"}
        aria-hidden="true"
      />
    </button>
  );
}
